import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 30000; // 30 seconds
const REMINDER_INTERVAL = 300000; // 5 minutes

// ---- Reliable alarm sound using HTML Audio element ----
let alarmAudio = null;
let audioUnlocked = false;

function getAlarmAudio() {
  if (!alarmAudio) {
    alarmAudio = new Audio('/alarm.wav');
    alarmAudio.preload = 'auto';
    alarmAudio.volume = 1.0;
  }
  return alarmAudio;
}

// Unlock audio on first user tap/click (mobile browser policy)
function unlockAudio() {
  if (audioUnlocked) return;
  const audio = getAlarmAudio();
  // Play silent then pause to unlock the audio element
  audio.volume = 0.01;
  const p = audio.play();
  if (p && p.then) {
    p.then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 1.0;
      audioUnlocked = true;
    }).catch(() => {});
  }
}

if (typeof window !== 'undefined') {
  const events = ['click', 'touchstart', 'touchend', 'keydown'];
  const handler = () => {
    unlockAudio();
    events.forEach(e => document.removeEventListener(e, handler, true));
  };
  events.forEach(e => document.addEventListener(e, handler, true));
}

function playAlarmSound() {
  // Vibrate first (works on Android even when audio is blocked / phone on silent)
  try {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate([400, 150, 400, 150, 400]);
    }
  } catch (e) { /* ignore */ }
  try {
    const audio = getAlarmAudio();
    audio.volume = 1.0;
    audio.currentTime = 0;
    const p = audio.play();
    if (p && p.then) {
      p.catch(err => {
        console.warn('Alarm play blocked:', err);
        // Retry once after a short delay (helps when audio context just woke up)
        setTimeout(() => {
          try {
            audio.currentTime = 0;
            const p2 = audio.play();
            if (p2 && p2.then) p2.catch(() => {});
          } catch (e) { /* ignore */ }
        }, 300);
      });
    }
  } catch (e) {
    console.warn('Could not play alarm sound:', e);
  }
}

export function useDispatchNotifications() {
  const { token, isAuthenticated, user } = useAuth();
  const location = useLocation();
  const [newStops, setNewStops] = useState([]);
  const [reminderCount, setReminderCount] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const reminderIntervalRef = useRef(null);
  const hasTriggeredRef = useRef(false);
  const previousNewIdsRef = useRef(new Set());
  const isDriverPage = location.pathname === '/orders';
  // Routes where the driver-side alarm must STAY SILENT even if a driver
  // session happens to be active in the same browser (typical for a dispatcher
  // who also has a driver account). Without this, the dispatcher's computer
  // ends up playing the alarm meant for the driver's phone.
  const isOnNonDriverApp = location.pathname.startsWith('/admin')
    || location.pathname.startsWith('/atelier')
    || location.pathname.startsWith('/client-portal')
    || location.pathname.startsWith('/presentation')
    || location.pathname.startsWith('/privacy')
    || location.pathname.startsWith('/terms');

  // Check if user is a driver (not admin) AND actively on the driver app
  const isDriver = isAuthenticated && user?.role === 'driver' && !isOnNonDriverApp;

  // Clear all notifications when visiting /orders
  useEffect(() => {
    if (isDriverPage && newStops.length > 0) {
      setShowNotification(false);
      setReminderCount(0);
      hasTriggeredRef.current = false;
      previousNewIdsRef.current = new Set(newStops.map(s => s.id));
      if (reminderIntervalRef.current) {
        clearInterval(reminderIntervalRef.current);
        reminderIntervalRef.current = null;
      }
    }
  }, [isDriverPage, newStops]);

  // Poll for new stops
  const checkNewStops = useCallback(async () => {
    if (!token || !isDriver) return;
    try {
      const response = await fetch(`${API_URL}/api/dispatch/driver/stops?status=envoye`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const stops = data.stops || [];
        setNewStops(stops);

        // Check if there are truly NEW stops (not previously seen)
        const currentIds = new Set(stops.map(s => s.id));
        const hasNewOnes = stops.some(s => !previousNewIdsRef.current.has(s.id));

        if (stops.length > 0 && hasNewOnes && !isDriverPage && !hasTriggeredRef.current) {
          // First detection of new stops
          hasTriggeredRef.current = true;
          setReminderCount(1);
          setShowNotification(true);
          playAlarmSound();

          // Start 5-minute reminder cycle
          if (reminderIntervalRef.current) clearInterval(reminderIntervalRef.current);
          reminderIntervalRef.current = setInterval(() => {
            setReminderCount(prev => {
              const next = prev + 1;
              playAlarmSound();
              setShowNotification(true);
              return next;
            });
          }, REMINDER_INTERVAL);
        }

        if (stops.length === 0) {
          // All stops acknowledged — clear everything
          hasTriggeredRef.current = false;
          setShowNotification(false);
          setReminderCount(0);
          previousNewIdsRef.current = new Set();
          if (reminderIntervalRef.current) {
            clearInterval(reminderIntervalRef.current);
            reminderIntervalRef.current = null;
          }
        }
      }
    } catch (e) {
      // Silent fail
    }
  }, [token, isDriver, isDriverPage]);

  // Polling effect
  useEffect(() => {
    if (!isDriver) return;
    checkNewStops();
    const interval = setInterval(checkNewStops, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [checkNewStops, isDriver]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reminderIntervalRef.current) {
        clearInterval(reminderIntervalRef.current);
      }
    };
  }, []);

  const dismissPopup = useCallback(() => {
    setShowNotification(false);
  }, []);

  return {
    newStopsCount: newStops.length,
    newStops,
    hasNewStops: newStops.length > 0,
    reminderCount,
    showNotification: showNotification && !isDriverPage,
    // 1-2 = popup, 3+ = fullscreen
    notificationType: reminderCount <= 2 ? 'popup' : 'fullscreen',
    dismissPopup,
  };
}
